import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CheckCircle2, MapPin, Calendar, Hash, Loader2, Mic, MicOff, ArrowLeft, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * In-bot bidding form for transporters. Mirrors the look-and-feel of
 * TripFormInBot but submits a quote (price + service type + duration + notes)
 * against an open trip request.
 *
 * Props:
 *  - request: the OpenRequestSummary object returned by
 *             GET /api/chat/transporter/open-requests
 *  - transporterUserId: HG-TR-XXXX of the signed-in transporter
 *  - onBack:    fn — return to the request list
 *  - onSubmitted: fn(quote) — called after a successful POST /api/quotes
 */
const BidFormInBot = ({ request, transporterUserId, onBack, onSubmitted }) => {
  const { t, i18n } = useTranslation();

  const [price, setPrice] = useState('');
  const [serviceType, setServiceType] = useState('standard');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Voice dictation for the notes field only (per user choice).
  const SpeechRecognition = (typeof window !== 'undefined') && (
    window.SpeechRecognition || window.webkitSpeechRecognition
  );
  const mediaRecorderSupported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator?.mediaDevices?.getUserMedia;
  const voiceSupported = !!SpeechRecognition || mediaRecorderSupported;
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const transportPrice = parseFloat(price) || 0;
  const transporterFee = +(transportPrice * 0.05).toFixed(2);
  const transporterEarnings = +(transportPrice - transporterFee).toFixed(2);

  // Static-map preview via backend proxy.
  const staticMapUrl = (request?.pickup_address && request?.dropoff_address)
    ? `${API}/maps/static-map?origin=${encodeURIComponent(request.pickup_address)}&destination=${encodeURIComponent(request.dropoff_address)}&width=360&height=140`
    : null;

  const langForRecognition = () => {
    const m = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', nl: 'nl-NL' };
    return m[i18n.language?.slice(0, 2)] || 'en-US';
  };

  const toggleVoice = () => {
    if (listening || transcribing) {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {
          // ignore
        }
      }
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try { recorderRef.current.stop(); } catch {
          // ignore
        }
      }
      setListening(false);
      return;
    }
    setError('');
    // Web Speech first.
    if (SpeechRecognition) {
      const rec = new SpeechRecognition();
      rec.lang = langForRecognition();
      rec.interimResults = true;
      rec.continuous = false;
      let finalT = '';
      rec.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const txt = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalT += txt;
          else interim += txt;
        }
        setNotes((finalT + interim).trim());
      };
      rec.onerror = () => setListening(false);
      rec.onend = () => setListening(false);
      try {
        rec.start();
        recognitionRef.current = rec;
        setListening(true);
      } catch {
        setListening(false);
      }
      return;
    }
    // Whisper fallback
    if (mediaRecorderSupported) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        streamRef.current = stream;
        const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
        let mimeType = '';
        for (const c of candidates) {
          if (window.MediaRecorder.isTypeSupported?.(c)) { mimeType = c; break; }
        }
        const rec = mimeType
          ? new window.MediaRecorder(stream, { mimeType })
          : new window.MediaRecorder(stream);
        chunksRef.current = [];
        rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
        rec.onstop = async () => {
          try { streamRef.current?.getTracks().forEach(tr => tr.stop()); } catch {
            // ignore
          }
          streamRef.current = null;
          setListening(false);
          const blobType = rec.mimeType || mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type: blobType });
          chunksRef.current = [];
          if (!blob.size) return;
          setTranscribing(true);
          try {
            const ext = blobType.includes('mp4') ? 'm4a' : blobType.includes('ogg') ? 'ogg' : 'webm';
            const fd = new FormData();
            fd.append('audio', blob, `bid-notes.${ext}`);
            fd.append('language', (i18n.language || 'en').slice(0, 2));
            const { data } = await axios.post(`${API}/chat/transcribe`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (data?.text) setNotes(prev => prev ? `${prev} ${data.text}`.trim() : data.text);
          } catch {
            // soft fail
          } finally {
            setTranscribing(false);
          }
        };
        recorderRef.current = rec;
        rec.start();
        setListening(true);
      }).catch(() => setListening(false));
    }
  };

  // Stop mic on unmount
  useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch {
      // ignore
    }
    try { recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop(); } catch {
      // ignore
    }
    try { streamRef.current?.getTracks().forEach(tr => tr.stop()); } catch {
      // ignore
    }
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (transportPrice <= 0) {
      setError(t('chat.bid.priceRequired') || 'Please enter a valid price in MAD.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, {
        trip_request_id: request.request_id,
        transporter_user_id: transporterUserId,
        transport_price: transportPrice,
        notes: notes || '',
        estimated_duration: duration || '',
        service_type: serviceType,
      });
      onSubmitted?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (t('chat.bid.submitError') || 'Could not submit your bid. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="bid-form-in-bot"
      className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-black/30"
    >
      <button
        type="button"
        onClick={onBack}
        data-testid="bid-form-back-btn"
        className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t('chat.bid.backToList') || 'Back to open requests'}
      </button>

      {/* Request summary card with optional map preview */}
      <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
        {staticMapUrl && (
          <img
            src={staticMapUrl}
            alt="route"
            className="w-full h-32 object-cover bg-black/40"
            data-testid="bid-form-map"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <div className="p-3 space-y-1.5 text-xs">
          <div className="text-amber-300 font-mono text-[10px]" data-testid="bid-form-request-id">
            {request.request_id}
          </div>
          <div className="flex items-start gap-1.5 text-white">
            <MapPin className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="leading-snug">
              <div>{request.pickup_address}</div>
              <div className="text-gray-400">→ {request.dropoff_address}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-gray-300 pt-1">
            {request.transport_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {request.transport_date}
              </span>
            )}
            {request.horse_count && (
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3" /> {request.horse_count} {(parseInt(request.horse_count, 10) || 0) === 1 ? 'horse' : 'horses'}
              </span>
            )}
          </div>
          {request.additional_comments && (
            <div className="text-gray-400 italic pt-1 border-t border-white/5 mt-2">
              &ldquo;{request.additional_comments}&rdquo;
            </div>
          )}
        </div>
      </div>

      {/* Bid form */}
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs text-gray-300 font-medium mb-1 block">
            {t('chat.bid.priceLabel') || 'Your price (MAD)'}
          </label>
          <input
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="2500"
            data-testid="bid-form-price"
            className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>

        <div>
          <label className="text-xs text-gray-300 font-medium mb-1 block">
            {t('chat.bid.serviceTypeLabel') || 'Service type'}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['standard', 'express'].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setServiceType(opt)}
                data-testid={`bid-form-service-${opt}`}
                className={`text-xs py-2 rounded-lg border transition-colors capitalize ${
                  serviceType === opt
                    ? 'bg-amber-500/20 border-amber-500/60 text-amber-200'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                {t(`chat.bid.service.${opt}`) || opt}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-300 font-medium mb-1 block">
            {t('chat.bid.durationLabel') || 'Estimated duration (optional)'}
          </label>
          <input
            type="text"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder={t('chat.bid.durationPlaceholder') || 'e.g. 6h'}
            data-testid="bid-form-duration"
            className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2 text-white text-sm"
            maxLength={40}
          />
        </div>

        <div>
          <label className="text-xs text-gray-300 font-medium mb-1 block">
            {t('chat.bid.notesLabel') || 'Notes (optional)'}
          </label>
          <div className="relative">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={listening
                ? (t('chat.voice.listening') || 'Listening… tap to stop')
                : transcribing
                  ? (t('chat.voice.transcribing') || 'Transcribing…')
                  : (t('chat.bid.notesPlaceholder') || 'e.g. 2-stall trailer with cameras, departing 5am')}
              data-testid="bid-form-notes"
              rows={3}
              maxLength={400}
              className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2 pr-10 text-white text-sm resize-none"
            />
            {voiceSupported && (
              <button
                type="button"
                onClick={toggleVoice}
                data-testid="bid-form-notes-mic"
                aria-label={listening ? 'Stop dictation' : 'Dictate notes'}
                title={transcribing
                  ? (t('chat.voice.transcribing') || 'Transcribing…')
                  : listening
                    ? (t('chat.voice.listening') || 'Listening… tap to stop')
                    : (t('chat.voice.dictate') || 'Dictate by voice')}
                className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                  listening
                    ? 'bg-red-500 hover:bg-red-400 text-white animate-pulse'
                    : transcribing
                      ? 'bg-amber-500/30 text-amber-200 animate-pulse'
                      : 'bg-white/5 hover:bg-white/15 text-emerald-300 border border-emerald-500/30'
                }`}
              >
                {listening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>

        {/* Live earnings preview */}
        {transportPrice > 0 && (
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs space-y-0.5"
            data-testid="bid-form-earnings"
          >
            <div className="flex justify-between text-gray-300">
              <span>{t('chat.bid.gross') || 'Gross'}</span>
              <span>{transportPrice.toLocaleString()} MAD</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>{t('chat.bid.fee') || '– Platform fee (5%)'}</span>
              <span>−{transporterFee.toLocaleString()} MAD</span>
            </div>
            <div className="flex justify-between text-emerald-300 font-semibold pt-1 border-t border-white/5">
              <span>{t('chat.bid.youEarn') || 'You earn'}</span>
              <span data-testid="bid-form-earnings-net">{transporterEarnings.toLocaleString()} MAD</span>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="bid-form-error" className="text-xs text-red-300 bg-red-500/5 border border-red-500/20 rounded-lg px-2 py-1.5">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !price}
          data-testid="bid-form-submit"
          className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-2 transition-colors"
        >
          {submitting
            ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('chat.bid.submitting') || 'Sending…'}</>
            : <><Send className="w-4 h-4" /> {t('chat.bid.submit') || 'Send bid'}</>}
        </button>
      </form>
    </div>
  );
};

export const BidSuccessCard = ({ quote, onDone }) => {
  const { t } = useTranslation();
  return (
    <div data-testid="bid-success-card" className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 space-y-2">
      <div className="flex items-center gap-2 text-emerald-300">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-semibold text-sm">{t('chat.bid.sent') || 'Bid sent!'}</span>
      </div>
      <p className="text-xs text-gray-300 leading-snug">
        {t('chat.bid.sentBody') || "The customer has been notified and will see your quote next time they open their dashboard."}
      </p>
      {quote?.quote_id && (
        <div className="text-[10px] text-emerald-200/70 font-mono">
          {t('chat.bid.quoteId') || 'Quote ID'}: {quote.quote_id}
        </div>
      )}
      <button
        type="button"
        onClick={onDone}
        data-testid="bid-success-done-btn"
        className="w-full mt-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 rounded-full py-2 text-xs font-medium transition-colors"
      >
        {t('chat.bid.viewMore') || 'Browse more open requests'}
      </button>
    </div>
  );
};

export default BidFormInBot;
